import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import { Button } from 'primereact/button';
import { DataView } from 'primereact/dataview';
import { Dialog } from 'primereact/dialog';
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Skeleton } from "primereact/skeleton";
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useGlobalHelpState } from "../../../help-context";
import { createInstallation } from "../../../service/ScrbService";
import { StructureContext } from "../../../structure-context";
import { globalUserState } from "../../../user-context";
import { allInstallations, currentInstallation, loadCurrentInstallation, loadInstallations, scrbData, scrollToTop } from "../ScrbTools";
import Installation from "./Installation";
import InstallationCard from "./InstallationCard";
import { Help } from "../../help/Help";
import { Sidebar } from "primereact/sidebar";
import AddContactDialog from "../informations/AddContactDialog";

export const ListInstallations = () => {

    const structure = useContext(StructureContext);
    const installation = useHookstate(currentInstallation);
    const installations = useHookstate(allInstallations);
    const userData = useHookstate(globalUserState).get();
    const data = useHookstate(scrbData).get();
    const [dialogCreateVisible, setDialogCreateVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useRef(null);
    const help = useGlobalHelpState();
    const [printMode, setPrintMode] = useState(null);

    const [filters, setFilters] = useState({
        referenceExterne: '',
        nom: '',
        genreInstallationId: '',
        localite: '',
        recStatusId: '',
        search: '',
        conforme: null,
    });

    useEffect(() => {
        help.set({ help: Help.installation, show: false });
        // eslint-disable-next-line
    }, []);

    const createNewInstallation = (selectedGenreInst) => {
        setDialogCreateVisible(false);
        const newInstallation = {
            genreInstallationId: selectedGenreInst.id,
            structureId: Number(structure.id),
            chaudiere: {},
            bruleur: {},
            address: {},
            addressOwner: {},
            ownerSame: true,
            bruleurSame: [1, 2].includes(selectedGenreInst.id),
            statut: 2,
            nouveauRdv: false,
            dateDemande: new Date().toISOString(),
            lastUpdateBy: "frontend"
        };
        installation.set(newInstallation);
    }

    const createNouveauRdv = async () => {
        installation["nouveauRdv"].set(true);
        setIsLoading(true);
        try {
            await createInstallation(installation.value);
            showNotif("success", "Nouvelle demande", "Nouvelle demande sauvegardée avec succès");
            await loadInstallations(structure.id);
            installation.set({});
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de l'installation:", error);
            showNotif("error", "Erreur lors de la sauvegarde de l'installation", error.message || "Une erreur inattendue s'est produite");
        } finally {
            setIsLoading(false);
            scrollToTop();
        }
    }

    const saveInstallation = async () => {
        installation["nouveauRdv"].set(false);
        setIsLoading(true);
        try {
            await createInstallation(installation.value);
            showNotif("success", "Installation sauvegardée", "Installation sauvegardée avec succès");
            await loadInstallations(structure.id);
            installation.set({});
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de l'installation:", error);
            showNotif("error", "Erreur lors de la sauvegarde de l'installation", error.message || "Une erreur inattendue s'est produite");
        } finally {
            setIsLoading(false);
            scrollToTop();
        }
    }

    const showNotif = (severity, summary, detail) => {
        toast.current?.show({
            severity: `${severity}`,
            summary: `${summary}`,
            detail: `${detail}`,
            life: 3000
        });
    }

    const onCancel = () => {
        installation.set({});
    }

    const installationView = () => {
        return (
            <Installation onSave={saveInstallation} onCancel={onCancel} showNotif={showNotif} onNouveauRdv={createNouveauRdv} />
        );
    }

    const dialogCreateInst = () => {
        return (
            <div className="flex justify-content-start">
                <Dialog
                    header={I18n.get("newInstallation")}
                    visible={dialogCreateVisible}
                    onHide={() => {
                        setDialogCreateVisible(false);
                    }}
                    dismissableMask
                    className="w-max"
                >
                    <Divider />
                    <div className={"flex flex-column lg:flex-row"}>
                        {data.genreInstList.map((genreInst) => {
                            const color = `border-200 bg-${data.installationColors[genreInst.id].brut}-400 hover:bg-${data.installationColors[genreInst.id].brut}-600`;
                            const userGroupes = userData[`cdm-groups:${structure.id}`].split(" ");
                            if (userGroupes.includes(data.genreInstList[genreInst.id - 1]?.groupMember)) {
                                return (
                                    <div key={genreInst.id} className="flex w-full justify-center pr-3">
                                        <Button
                                            onClick={() => createNewInstallation(genreInst)}
                                            className={`${color} flex border w-full lg:w-10rem lg:h-10rem mb-2 lg:mb-0 justify-content-center align-items-center`}
                                        >
                                            <div className="flex flex-column align-items-center text-lg font-semibold">
                                                {I18n.get(genreInst.groupMember)}
                                                <i className={data.installationColors[genreInst.id].icon} style={{ fontSize: '1.5rem' }}></i>
                                            </div>
                                        </Button>
                                    </div>
                                )
                            } else return <div key={genreInst.id}></div>
                        })}
                    </div>
                    <Message
                        className="mt-3"
                        style={{ width: '55rem' }}
                        text={
                            <>{I18n.get("textPompe")}
                                &nbsp;<a href="https://legilux.public.lu/filestore/eli/etat/leg/rgd/2025/08/13/a395/jo/fr/pdfa/eli-etat-leg-rgd-2025-08-13-a395-jo-fr-pdfa.pdf" target="_blank" rel="noreferrer" className="fa-solid fa-arrow-up-right-from-square"></a>
                            </>}
                    />
                </Dialog>
            </div>
        )
    }

    const skeletonInstallations = () => {
        return <div className="flex flex-wrap">
            {Array.from({ length: 15 }).map((_, index) => (
                <Skeleton key={index} className="m-2 w-12 h-4rem"></Skeleton>
            ))}
        </div>
    }

    const handleInstallationClick = (instId) => {
        setIsLoading(true);
        loadCurrentInstallation(structure.id, instId, () => {
            setIsLoading(false);
        });
    };

    const listTemplate = (installations) => {
        return <div className="flex flex-column ">
            {installations.map((inst) =>
                <button
                    key={inst.id}
                    className="text-left pb-2 border-none bg-transparent"
                    onClick={() => handleInstallationClick(inst.id)}
                    style={{ cursor: 'pointer' }}
                >
                    <InstallationCard installation={inst} mode="installation" onNouveauRdv={createNouveauRdv} />
                </button>)}
        </div>;
    };

    const printView = () => {
        const tournee = printMode.sort((a, b) => b.referenceExterne - a.referenceExterne);
        setTimeout(() => {  
            window.print();
            setPrintMode(null);
        }, 1000);
        return tournee && <Sidebar visible={printMode !== null} onHide={() => setPrintMode(null)} fullScreen>
            <div className="pl-4 pt-6">
                {
                    tournee?.map((item) => (
                        <InstallationCard
                            key={item.referenceExterne}
                            installation={item}
                            mode="installation"
                            printMode={true}
                            viewMode={true}
                            dragging={false}
                        />
                    ))
                }
            </div >
        </Sidebar>
    }

    const applyFilters = (installations) => {
        return JSON.parse(JSON.stringify(installations)).filter(inst => {
            const fullAddress = inst.address.number + " " + inst.address.street + ", " + inst.address.codePostal + " " + inst.address.localite;
            const searchMatch = !filters.search || [
                inst.referenceExterne.toString(),
                inst.nom,
                fullAddress,
            ].some(field => field?.toLowerCase().includes(filters.search?.toLowerCase()));
            const conformeMatch = filters.conforme === null || inst.isConforme === filters.conforme;
            return searchMatch &&
                (filters.genreInstallationId === '' || inst.genreInstallationId === filters.genreInstallationId) &&
                (filters.recStatusId === '' || (inst.statut && Number(filters.recStatusId) === inst.statut)) &&
                conformeMatch;
        });
    };

    const filtersView = () => {
        const genreInstItemTemplate = (option) => {
            return option ? (
                <div className="flex align-items-center">
                    <i className={data.installationColors[option.id].icon + " text-" + data.installationColors[option.id].brut + "-500"} style={{ fontSize: '1rem' }}></i>
                    <span className="ml-2">{I18n.get(option.groupMember)}</span>
                </div>
            ) : I18n.get("genreInstallation");
        };
        return (
            <div className="flex flex-column gap-2">
                <div className="border-100 border-1px border-round p-2 surface-200">
                    {I18n.get("status")}
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value='' checked={filters.recStatusId === ''} onChange={(e) => setFilters({ ...filters, recStatusId: e.value })} />
                    <label>
                        {I18n.get("all")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={1} checked={Number(filters.recStatusId) === 1} onChange={(e) => setFilters({ ...filters, recStatusId: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-gray-500 mr-2" />
                        {I18n.get("nonDemande")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={2} checked={Number(filters.recStatusId) === 2} onChange={(e) => setFilters({ ...filters, recStatusId: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-yellow-500 mr-2" />
                        {I18n.get("nonPlanifie")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={3} checked={Number(filters.recStatusId) === 3} onChange={(e) => setFilters({ ...filters, recStatusId: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-red-500 mr-2" />
                        {I18n.get("nonConfirme")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={5} checked={Number(filters.recStatusId) === 5} onChange={(e) => setFilters({ ...filters, recStatusId: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-green-500 mr-2" />
                        {I18n.get("confirme")}
                    </label>
                </div>
                <div className="border-100 border-1px border-round p-2 surface-200">
                    {I18n.get("genreInstallation")}
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={''} checked={filters.genreInstallationId === ''} onChange={(e) => setFilters({ ...filters, genreInstallationId: e.value })} />
                    <label>
                        {I18n.get("all")}
                    </label>
                </div>
                {installations && data.genreInstList && JSON.parse(JSON.stringify(data.genreInstList))
                    .filter(item => installations.get().some(inst => inst.genreInstallationId === item.id))
                    .sort((a, b) => I18n.get(a.label).localeCompare(I18n.get(b.label)))
                    .map((genreInst) => {
                        return (
                            <div key={genreInst.id} className="flex flex-row align-items-center gap-2">
                                <RadioButton value={genreInst.id} checked={filters.genreInstallationId === genreInst.id} onChange={(e) => setFilters({ ...filters, genreInstallationId: e.value })} />
                                <label>
                                    {genreInstItemTemplate(genreInst)}
                                </label>
                            </div>
                        )
                    })}
                <div className="border-100 border-1px border-round p-2 surface-200">
                    {I18n.get("conformite")}
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={null} checked={filters.conforme === null} onChange={(e) => setFilters({ ...filters, conforme: e.value })} />
                    <label>
                        {I18n.get("all")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={1} checked={filters.conforme === 1} onChange={(e) => setFilters({ ...filters, conforme: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-green-500 mr-2" />
                        {I18n.get("conforme")}
                    </label>
                </div>
                <div className="flex flex-row align-items-center gap-2">
                    <RadioButton value={0} checked={filters.conforme === 0} onChange={(e) => setFilters({ ...filters, conforme: e.value })} />
                    <label>
                        <i className="fa-solid fa-circle text-red-500 mr-2" />
                        {I18n.get("nonConforme")}
                    </label>
                </div>
            </div >
        )
    }

    const listInstallationsView = () => {
        return (
            <div className="card">
                <div className="flex justify-content-between align-items-center">
                    <h5>  {I18n.get("mesInstallations")}</h5>
                    <div className="flex flex-row align-items-center gap-2">
                        <Button
                            icon="pi pi-print"
                            label={I18n.get("print")}
                            className="p-button-outlined mr-3"
                            onClick={() => setPrintMode(applyFilters(installations.get()))}
                        />
                        <Button
                            label={I18n.get("new")}
                            icon="pi pi-plus"
                            className="p-button-outlined mr-3"
                            onClick={() => setDialogCreateVisible(true)}
                        />
                    </div>
                </div>
                <hr />
                {dialogCreateInst()}
                {isLoading ? skeletonInstallations() : (
                    <div className="flex flex-column lg:flex-row-reverse">
                        <div id="filterBloc" className="w-full lg:w-4 mb-3 lg:mb-0 lg:ml-3 order-1 lg:order-2 dynamic-filters filtrer-bloc">
                            {filtersView()}
                        </div>
                        <Divider layout="vertical" className="hidden lg:flex order-2 lg:order-2" />
                        <div id="filterButtonDiv">
                            <Button
                                icon="pi pi-filter"
                                label={I18n.get("filtrer")}
                                text
                                onClick={() => {
                                    document.getElementById("filterBloc").classList.remove("filtrer-bloc");
                                    document.getElementById("filterButtonDiv").classList.add("filtrer-bloc");
                                }}
                                className="text-gray-500 filtrer-button"
                            />
                        </div>
                        <div className="w-full lg:w-9 order-3 lg:order-1">
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" style={{ left: '0.5rem' }} />
                                <InputText
                                    value={filters.search || ''}
                                    placeholder={I18n.get("search")}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                    className="w-full pl-4"
                                />
                            </span>
                            <DataView
                                emptyMessage={I18n.get("noInstallationAvailable")}
                                value={applyFilters(installations.get())}
                                sortField="referenceExterne"
                                sortOrder={-1}
                                listTemplate={listTemplate}
                                className="pt-2"
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return printMode !== null ? printView() : (
        <div>
            <Toast ref={toast} />
            <AddContactDialog autoOpenIfEmpty showNotif={showNotif} />
            {(() => {
                if (installation.get().genreInstallationId)
                    return installationView();
                else
                    return listInstallationsView();
            })()}
        </div>
    )
}

export default ListInstallations;